/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState, useEffect } from 'react';
import Page from 'components/custom/template/Page';
import { useNavigate } from 'react-router-dom';
import apiEndpoints from 'constants/apiEndpoints';
import SeparatedEmployeeColumns from './SeparatedEmployeeColumns';
import { hrmsPaths } from 'constants/pagePath';
import useHandleChangeStatus from 'hooks/custom/useHandleChangeStatus';
import useManagePageLoading from 'hooks/custom/useManagePageLoading';
import useHandleDelete from 'hooks/custom/useHandleDelete';
import { useAlert } from 'providers/AlertModalProvider';
import postData from 'services/postData';
import { Modal } from 'react-bootstrap';
import Button from 'components/base/Button';

const dummyEmployees = [
  {
    employee_id: 1,
    employee_image: '/assets/img/avatars/default.png',
    first_name: 'John',
    last_name: 'Doe',
    employee_mobile: '9876543210',
    employee_unique_id: 'EMP001',
    date_of_birth: '1990-05-15',
    designation_name: 'Employee',
    department_name: 'Software Development',
    joining_date: '2020-01-10',
    separation_date: '2023-11-25',
    separation_reason: 'Resignation',
    status: 'Inactive',
    duration: '1 Years 4 Month',
    percentage: 100
  },
  {
    employee_id: 2,
    employee_image: '/assets/img/avatars/default.png',
    first_name: 'Jane',
    last_name: 'Smith',
    employee_mobile: '9876543211',
    employee_unique_id: 'EMP002',
    date_of_birth: '1992-08-20',
    designation_name: 'Employee',
    department_name: 'Human Resources',
    joining_date: '2019-03-15',
    separation_date: '2023-10-30',
    separation_reason: 'Better Opportunity',
    status: 'Inactive',
    duration: '2 Years 2 Month',
    percentage: 100
  },
  {
    employee_id: 3,
    employee_image: '/assets/img/avatars/default.png',
    first_name: 'Michael',
    last_name: 'Johnson',
    employee_mobile: '9876543212',
    employee_unique_id: 'EMP003',
    date_of_birth: '1988-11-12',
    designation_name: 'Employee',
    department_name: 'Finance',
    joining_date: '2018-07-05',
    separation_date: '2023-12-15',
    separation_reason: 'Contract End',
    status: 'Inactive',
    duration: '6 Years 4 Month',
    percentage: 100
  },
  {
    employee_id: 4,
    employee_image: '/assets/img/avatars/default.png',
    first_name: 'Sarah',
    last_name: 'Williams',
    employee_mobile: '9876543213',
    employee_unique_id: 'EMP004',
    date_of_birth: '1993-04-25',
    designation_name: 'Employee',
    department_name: 'Marketing',
    joining_date: '2021-02-10',
    separation_date: '2024-01-20',
    separation_reason: 'Relocation',
    status: 'Inactive',
    duration: '2 Years 4 Month',
    percentage: 100
  },
  {
    employee_id: 5,
    employee_image: '/assets/img/avatars/default.png',
    first_name: 'Robert',
    last_name: 'Brown',
    employee_mobile: '9876543214',
    employee_unique_id: 'EMP005',
    date_of_birth: '1987-09-18',
    designation_name: 'Employee',
    department_name: 'Operations',
    joining_date: '2017-11-15',
    separation_date: '2023-09-30',
    separation_reason: 'Early Retirement',
    status: 'Inactive',
    duration: '7 Years 8 Month',
    percentage: 100
  }
];
const SeparatedEmployee = () => {
  const refreshDataRef = useRef(null);
  const navigate = useNavigate();
  const [pageLoading, loadingMessage, handleLoading] = useManagePageLoading();
  const employeeDelete = useHandleDelete();
  const { showAlert } = useAlert();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showRejoinModal, setShowRejoinModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [newUnblockPin, setNewUnblockPin] = useState('');
  const employeeStatusChange = useHandleChangeStatus();
  const [employeeData, setEmployeeData] = useState([]);

  // Simulate API data loading with dummy data
  useEffect(() => {
    // Simulate loading delay
    handleLoading(true, 'Loading separated employees');
    setTimeout(() => {
      setEmployeeData(dummyEmployees);
      handleLoading(false);
    }, 800);
  }, []);

  const handleRefreshDataRegister = refreshData => {
    refreshDataRef.current = refreshData;
  };

  const handleRefresh = () => {
    if (refreshDataRef.current) {
      refreshDataRef.current();
    } else {
      // Fallback for dummy data scenario
      handleLoading(true, 'Refreshing data');
      setTimeout(() => {
        setEmployeeData([...dummyEmployees]);
        handleLoading(false);
      }, 500);
    }
  };

  const handleChangeStatus = async employee => {
    employeeStatusChange({
      label: 'Employee',
      name: employee.first_name,
      status: employee.status,
      url: apiEndpoints.employee.UpdateStatus,
      handleLoading,
      handleRefresh,
      payLoad: {
        status: employee.status === 'Active' ? 'In-active' : 'Active',
        employee_id: employee.employee_id
      }
    });
  };

  const handleDeleteEmployee = async employee => {
    employeeDelete({
      label: 'Employee',
      name: employee.first_name,
      url: apiEndpoints.employee.delete(employee.employee_id),
      handleLoading,
      handleRefresh,
      payLoad: {
        employee_id: employee.employee_id
      }
    });
  };

  const handleRejoinEmployee = employee => {
    setSelectedEmployee(employee);
    setShowRejoinModal(true);
  };

  const confirmRejoin = async () => {
    handleLoading(true, 'Processing rejoin request');

    // Simulate API call for rejoining employee
    setTimeout(() => {
      setShowRejoinModal(false);

      // Generate a random 6-digit pin for demonstration
      const newPin = Math.floor(100000 + Math.random() * 900000).toString();
      setNewUnblockPin(newPin);
      setShowSuccessModal(true);

      handleLoading(false);
    }, 1500);
  };

  const actions = original => ({
    view: {
      label: 'View',
      action: () =>
        navigate(`${hrmsPaths.employee.detail(original.employee_id)}`)
    },
    edit: {
      label: 'Edit',
      action: () =>
        navigate(`${hrmsPaths.employee.update(original.employee_id)}?tab=${1}`)
    },
    actionDelete: {
      label: 'Delete',
      action: () => handleDeleteEmployee(original)
    },
    otherActions: [
      {
        label: 'Rejoin',
        icon: 'fluent:arrow-reset-32-filled',
        iconClassName: 'me-1',
        action: () => handleRejoinEmployee(original)
      }
    ]
  });

  // Custom data fetcher to work with dummy data
  const customFetcher = async options => {
    return {
      status: true,
      data: {
        data: employeeData,
        total: employeeData.length
      }
    };
  };

  return (
    <Page
      title={'Separated Employee List'}
      tableColumn={{ column: SeparatedEmployeeColumns, actions }}
      backdrop={{ loading: pageLoading, message: loadingMessage }}
      // urlEndPoint={apiEndpoints.employee.getAll}
      exportName="separated employee list"
      // addNew={() => navigate(hrmsPaths.employee.create)}
      addBtnText="Add Employee"
      onRegisterRefresh={handleRefreshDataRegister}
      customFetcher={customFetcher} // Use custom fetcher for dummy data
      dummyData={dummyEmployees}
    >
      {/* Rejoin Confirmation Modal */}
      <Modal
        show={showRejoinModal}
        onHide={() => setShowRejoinModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Rejoin</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to rejoin {selectedEmployee?.first_name}{' '}
          {selectedEmployee?.last_name}? This will reactivate their account and
          create a new PIN.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejoinModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmRejoin}>
            Confirm Rejoin
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal with New PIN */}
      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Employee Rejoined Successfully</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The employee has been successfully rejoined.</p>
          <p>
            The new PIN is: <span className="fw-bold">{newUnblockPin}</span>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowSuccessModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </Page>
  );
};

export default SeparatedEmployee;
